import type { CallEndpoint } from '@wtfalch/calls/browser';

import { parse } from '../routes.ts';

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput';
export const DEVICE_KINDS: DeviceKind[] = ['audioinput', 'videoinput', 'audiooutput'];

/** The open agora, read off the address bar the way `ThreadPane` does, so the
 *  three call entry points need no new prop. Two agoras on one origin keep
 *  separate choices. */
function storageKey(): string {
  const route = parse(window.location.pathname);
  return `wtfalch-agora:${route.kind === 'agora' ? route.agoraId : ''}:call-devices`;
}

/** The last device chosen per kind. Storage can be blocked or hold junk, and
 *  either reads as "no choice". */
export function rememberedDevices(): Partial<Record<DeviceKind, string>> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey()) ?? '{}');
    const out: Partial<Record<DeviceKind, string>> = {};
    for (const kind of DEVICE_KINDS) {
      const id = (parsed as Record<string, unknown> | null)?.[kind];
      if (typeof id === 'string' && id) out[kind] = id;
    }
    return out;
  } catch {
    return {};
  }
}

export function rememberDevice(kind: DeviceKind, deviceId: string): void {
  if (!deviceId) return;
  try {
    window.localStorage.setItem(
      storageKey(),
      JSON.stringify({ ...rememberedDevices(), [kind]: deviceId }),
    );
  } catch {
    // Not remembering is the only cost.
  }
}

/** Applies each remembered choice whose device is still plugged in. A device
 *  that is gone, or any failure, leaves the system default and says nothing. */
export async function applyRememberedDevices(endpoint: CallEndpoint): Promise<void> {
  try {
    const remembered = rememberedDevices();
    if (DEVICE_KINDS.every((kind) => !remembered[kind])) return;
    const present = await endpoint.devices();
    for (const kind of DEVICE_KINDS) {
      const id = remembered[kind];
      if (!id || !present.some((d) => d.kind === kind && d.deviceId === id)) continue;
      // biome-ignore lint/correctness/useHookAtTopLevel: `useDevice` is the SDK's method name, not a hook
      await endpoint.useDevice(kind, id).catch(() => {});
    }
  } catch {
    // media_unsupported or a failed listing: keep the defaults.
  }
}
